import type { Device } from '../types';

export type CameraPresentationState = 'online' | 'offline' | 'checking' | 'stale';
export type CameraConnectionState = 'online' | 'offline' | 'checking';
export type CameraFreshnessState = 'fresh' | 'stale' | 'unknown';

export const CAMERA_HEARTBEAT_FRESHNESS_MS = 5 * 60_000;

const parseTimestamp = (value?: string | null) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) || timestamp <= 0 ? null : timestamp;
};

export const formatCameraAge = (ageMilliseconds: number | null) => {
  if (ageMilliseconds == null) return 'Нет данных';
  const seconds = Math.max(0, Math.floor(ageMilliseconds / 1_000));
  if (seconds < 60) return `${seconds} сек назад`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} мин назад`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ч назад`;
  return `${Math.floor(hours / 24)} д назад`;
};

export const getCameraPresentationStatus = (device: Device, now = Date.now()) => {
  const camera = device.cameraState;
  const heartbeatTimestamp = parseTimestamp(
    camera?.lastHeartbeatUtc ?? device.lastHeartbeatUtc ?? device.lastSeen,
  );
  const heartbeatAgeMilliseconds = heartbeatTimestamp == null
    ? camera?.heartbeatAgeSeconds == null
      ? null
      : Math.max(0, camera.heartbeatAgeSeconds * 1_000)
    : Math.max(0, now - heartbeatTimestamp);

  const freshness: CameraFreshnessState = heartbeatAgeMilliseconds == null
    ? 'unknown'
    : heartbeatAgeMilliseconds > CAMERA_HEARTBEAT_FRESHNESS_MS
      ? 'stale'
      : 'fresh';

  const isWaitingForFirstCheck =
    freshness === 'unknown' ||
    camera?.overallStatus === 'checking' ||
    (!camera && (device.backendStatus === 0 || device.backendStatus === 1));
  const connection: CameraConnectionState = isWaitingForFirstCheck
    ? 'checking'
    : device.isOnline
      ? 'online'
      : 'offline';

  const state: CameraPresentationState = connection === 'checking'
    ? 'checking'
    : freshness === 'stale'
      ? 'stale'
      : connection;

  const labels: Record<CameraPresentationState, string> = {
    online: 'В сети',
    offline: 'Нет связи',
    checking: 'Проверка',
    stale: 'Данные устарели',
  };

  const connectionLabels: Record<CameraConnectionState, string> = {
    online: 'В сети',
    offline: 'Нет связи',
    checking: 'Проверка',
  };

  const freshnessLabels: Record<CameraFreshnessState, string> = {
    fresh: 'Данные актуальны',
    stale: 'Данные устарели',
    unknown: 'Проверка',
  };

  return {
    state,
    label: labels[state],
    connection,
    connectionLabel: connectionLabels[connection],
    freshness,
    freshnessLabel: freshnessLabels[freshness],
    heartbeatTimestamp,
    heartbeatAgeMilliseconds,
    heartbeatAgeLabel: formatCameraAge(heartbeatAgeMilliseconds),
  };
};
